import * as R from 'ramda';
import { create } from 'zustand';
import { Order } from '../../modules/orders/order.types';
import { CopiedGrid } from '../../modules/orders/orderState.types';
import { IProductDetailsGridParams } from '../../services/modules/productDetails/productDetails.types';
import { OrderFilterStateEnum } from '../ProductDetailsFilter/productDetailsFilter.types';
import {
  IProductDetailsModal,
  MassActionCombinationItem,
  OpenedByOrderBookParams,
  PasteGridMassAction,
  ProductDetailsOrigin,
  ProductDetailsState,
  ProductDetailsStore,
  SetPasteGridMassActionParams,
} from './productDetails.types';
import { toggleItemToPasteGrid } from './utils/pasteGridMassAction.utils';

const EMPTY_PASTE_GRID_MASS_ACTION: PasteGridMassAction = {
  isAllItemsSelected: false,
  items: {},
  unselectedItems: {},
};

export const initialState: ProductDetailsState = {
  finishedOrder: undefined,
  isMobileHeaderFixed: false,
  hasSuggestedProducts: undefined,
  ordersFilterState: OrderFilterStateEnum.SELECTED_ORDER,
  relatedProductsFilterState: false,
  copiedGrid: { grid: [], product: undefined },
  cleanOrderId: { orderId: undefined, flag: false },
  openedByOrderBook: { state: false, productColorId: undefined, kitId: undefined, from: undefined },
  from: undefined,
  productDetailsModalState: {
    isOpen: false,
    productColorId: undefined,
    productId: undefined,
    disableGrid: false,
    kitId: undefined,
  },
  productGridParams: {
    orderIds: undefined,
    productColorId: undefined,
    availability: undefined,
    isFinishedOrder: false,
    deliveryWindowIds: undefined,
  },
  pasteGridMassAction: EMPTY_PASTE_GRID_MASS_ACTION,
  pastedCombinationKeys: [],
  mediaProductColorId: undefined,
};

const useProductDetailsStore = create<ProductDetailsStore>((set) => ({
  ...initialState,

  handleChangeProductGridParams: (productGridParams: IProductDetailsGridParams) =>
    set({ productGridParams }),

  handleOpenProductDetailsModal: (
    productGridParams: IProductDetailsGridParams,
    filterByRelatedProducts = false,
  ) => {
    set({ productGridParams, relatedProductsFilterState: filterByRelatedProducts });
  },

  handleChangeOrderFilterState: (state: OrderFilterStateEnum) => set({ ordersFilterState: state }),

  toggleRelatedProductsFilterState: (newState: boolean) =>
    set({ relatedProductsFilterState: newState }),

  resetFilters: () => set({ productGridParams: { ...initialState.productGridParams } }),

  resetFilterWithOrders: () =>
    set({ ...initialState, ordersFilterState: OrderFilterStateEnum.SELECTED_ORDER }),

  setCleanOrderId: (orderId: { orderId: string; flag: boolean }) => set({ cleanOrderId: orderId }),

  setProductDetailsModalState: (productDetailsModalState: IProductDetailsModal) => {
    set({
      productDetailsModalState,
      mediaProductColorId: undefined,
      hasSuggestedProducts: undefined,
    });
  },

  setMediaProductColorId: (mediaProductColorId?: string) => set({ mediaProductColorId }),

  setHasSuggestedProducts: (state?: boolean) => set({ hasSuggestedProducts: state }),

  setOpenedByOrderBook: (params: OpenedByOrderBookParams) => set({ openedByOrderBook: params }),

  setFrom: (from?: ProductDetailsOrigin) => set({ from }),

  setFinishedOrder: (order?: Order) => set({ finishedOrder: order }),

  setIsMobileHeaderFixed: (flag: boolean) => set({ isMobileHeaderFixed: flag }),

  resetProductDetailsStore: () => set({ ...initialState }),

  copyGrid: (grid: CopiedGrid) =>
    set({
      copiedGrid: { ...grid },
      pastedCombinationKeys: [],
      pasteGridMassAction: EMPTY_PASTE_GRID_MASS_ACTION,
    }),

  markCombinationAsPasted: (combinationKey: string) =>
    set((state) => ({
      pastedCombinationKeys: R.union(state.pastedCombinationKeys, [combinationKey]),
    })),

  removeCopiedGrid: () =>
    set({
      copiedGrid: { grid: [], product: undefined },
      pasteGridMassAction: EMPTY_PASTE_GRID_MASS_ACTION,
      pastedCombinationKeys: [],
    }),

  resetPasteGridMassAction: () => set({ pasteGridMassAction: EMPTY_PASTE_GRID_MASS_ACTION }),

  setPasteGridMassAction: (newState: SetPasteGridMassActionParams) => {
    if (!R.isEmpty(newState.unselectedItems) && !R.isEmpty(newState.items)) {
      return;
    }
    const { pasteGridMassAction } = useProductDetailsStore.getState();

    if (
      (!R.isEmpty(newState.unselectedItems) && !R.isEmpty(pasteGridMassAction.items)) ||
      (!R.isEmpty(newState.items) && !R.isEmpty(pasteGridMassAction.unselectedItems))
    ) {
      return;
    }

    set({ pasteGridMassAction: newState });
  },

  toggleCombinationMassActionSelection: (item: MassActionCombinationItem) => {
    const { pasteGridMassAction } = useProductDetailsStore.getState();
    const { isAllItemsSelected, items, unselectedItems } = pasteGridMassAction;
    const isUnselectMode = isAllItemsSelected || !R.isEmpty(unselectedItems);

    if (!isUnselectMode) {
      return set({
        pasteGridMassAction: {
          ...pasteGridMassAction,
          items: toggleItemToPasteGrid(item, items),
        },
      });
    }

    const newUnselectedItems = toggleItemToPasteGrid(item, unselectedItems);

    return set({
      pasteGridMassAction: {
        ...pasteGridMassAction,
        unselectedItems: newUnselectedItems,
        isAllItemsSelected: R.isEmpty(newUnselectedItems),
      },
    });
  },
}));

export default useProductDetailsStore;
